import { isWebSocketConnectEnabled } from 'firebase';
import { connect } from './connect';

type MessageSubscriber = (event: MessageEvent) => void;
type ConnectionSubscriber = () => void;

let socket: WebSocket | null = null;
let pendingRegistration: Promise<WebSocket | null> | null = null;
let unloadListener: (() => void) | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
let registrationGeneration = 0;
let shouldReconnect = false;
let currentUserID = '';
let currentOrganizationID: string | undefined;

const messageSubscribers = new Set<MessageSubscriber>();
const connectionSubscribers = new Set<ConnectionSubscriber>();

const cleanupUnloadListener = () => {
  if (typeof window !== 'undefined' && unloadListener) {
    window.removeEventListener('beforeunload', unloadListener);
    unloadListener = null;
  }
};

const clearReconnectTimer = () => {
  if (typeof window !== 'undefined' && reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
  }
  reconnectTimer = null;
};

const scheduleReconnect = () => {
  if (!shouldReconnect || !currentUserID || typeof window === 'undefined') {
    return;
  }
  clearReconnectTimer();
  const delay = Math.min(1000 * 2 ** reconnectAttempts, 30_000);
  reconnectAttempts += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    void registerWebSocket(currentUserID, currentOrganizationID);
  }, delay);
};

const closeCurrentSocket = (reason: string) => {
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    socket.close(1000, reason);
  }
  socket = null;
};

export const registerWebSocket = async (
  userID: string,
  organizationID?: string
): Promise<WebSocket | null> => {
  if (typeof window === 'undefined' || !userID) return null;
  if (!isWebSocketConnectEnabled()) return null;

  const sameIdentity =
    currentUserID === userID &&
    currentOrganizationID === (organizationID || undefined);
  if (
    sameIdentity &&
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return socket;
  }
  if (!sameIdentity) {
    registrationGeneration += 1;
    pendingRegistration = null;
    clearReconnectTimer();
    closeCurrentSocket('user logout');
    cleanupUnloadListener();
  }

  currentUserID = userID;
  currentOrganizationID = organizationID || undefined;
  shouldReconnect = true;

  if (pendingRegistration) return pendingRegistration;

  const generation = ++registrationGeneration;
  const registration: Promise<WebSocket | null> = (async () => {
    try {
      const nextSocket = await connect({ organizationID });
      if (generation !== registrationGeneration || !shouldReconnect) {
        nextSocket.close(1000, 'connection superseded');
        return null;
      }

      socket = nextSocket;
      nextSocket.addEventListener(
        'open',
        () => {
          if (socket !== nextSocket) return;
          reconnectAttempts = 0;
          connectionSubscribers.forEach((subscriber) => subscriber());
          nextSocket.send(
            JSON.stringify({ action: 'ping', userID, timestamp: Date.now() })
          );
        },
        { once: true }
      );
      nextSocket.addEventListener('message', (event) => {
        messageSubscribers.forEach((subscriber) => subscriber(event));
      });
      nextSocket.addEventListener('close', () => {
        if (socket === nextSocket) {
          socket = null;
          cleanupUnloadListener();
          scheduleReconnect();
        }
      });

      cleanupUnloadListener();
      unloadListener = () => closeWebSocket();
      window.addEventListener('beforeunload', unloadListener);
      return nextSocket;
    } catch {
      scheduleReconnect();
      return null;
    } finally {
      if (registrationGeneration === generation) pendingRegistration = null;
    }
  })();
  pendingRegistration = registration;
  return registration;
};

export const closeWebSocket = () => {
  shouldReconnect = false;
  currentUserID = '';
  currentOrganizationID = undefined;
  registrationGeneration += 1;
  pendingRegistration = null;
  clearReconnectTimer();
  closeCurrentSocket('user logout');
  cleanupUnloadListener();
};

export const getWebSocket = (): WebSocket | null => socket;

export const subscribeToWebSocketMessages = (
  subscriber: MessageSubscriber
): (() => void) => {
  messageSubscribers.add(subscriber);
  return () => messageSubscribers.delete(subscriber);
};

export const subscribeToWebSocketConnection = (
  subscriber: ConnectionSubscriber
): (() => void) => {
  connectionSubscribers.add(subscriber);
  return () => connectionSubscribers.delete(subscriber);
};
